import { useState } from 'react'
import { activeGroupLabels } from '@/config/navigation'

interface Seen {
  open: boolean
  pathname: string
}

/**
 * Which navigation groups are disclosed, and for how long.
 *
 * An unpinned rail is a menu, not a panel: it dismisses itself on every
 * navigation, so the submenus it was showing are dismissed with it. Closing
 * therefore discards the user's disclosures, and the next reveal rebuilds them
 * from the route alone. That keeps every top-level row in the same place each
 * time the rail is summoned and stops disclosures accumulating into a wall
 * with no way back. A pinned rail is part of the layout, never closes, and so
 * keeps whatever the user opened — a route change only ever reveals the
 * destination's group beside it.
 *
 * `null` is "closed, so rebuild from the route" rather than a set captured at
 * close: selecting a destination collapses the rail one render before the data
 * router commits the new location, so a set captured then would describe the
 * page the user just left. Deriving on read always sees the settled route.
 *
 * State is adjusted during render rather than in an effect so the rail never
 * paints a frame of the disclosures it is about to drop.
 */
export function useNavGroupDisclosure(open: boolean, pathname: string) {
  const [expanded, setExpanded] = useState<ReadonlySet<string> | null>(null)
  const [seen, setSeen] = useState<Seen>({ open, pathname })
  const routeGroups = activeGroupLabels(pathname)

  if (seen.open !== open || seen.pathname !== pathname) {
    setSeen({ open, pathname })
    if (seen.open && !open) setExpanded(null)
    else if (open && expanded && seen.pathname !== pathname)
      setExpanded(new Set([...expanded, ...routeGroups]))
  }

  return {
    expanded: expanded ?? new Set(routeGroups),
    toggle: (label: string) =>
      setExpanded((current) => {
        const next = new Set(current ?? routeGroups)
        if (!next.delete(label)) next.add(label)
        return next
      }),
    // Opening a group from the collapsed rail is a reveal, never a toggle: the
    // route's own group is already disclosed and must not close under the click.
    reveal: (label: string) =>
      setExpanded((current) => new Set(current ?? routeGroups).add(label)),
  }
}
